import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { v4 as uuidv4 } from "uuid";
import { LuX } from "react-icons/lu";
import {
  SqTooltip,
  SqTooltipTrigger,
  SqTooltipContent,
  SqTooltipProvider,
} from "@/components/squircle-ui/tooltip";
import { withCacheBuster } from "@/components/common/adCard/Media";

export interface SelectedAdAttachment {
  id: string;
  thumbnailUrl: string;
  accountName: string;
  accountIconUrl?: string | null;
}

const SelectedAdsPreview = ({
  ads,
  className = "",
  onRemove,
}: {
  ads: SelectedAdAttachment[];
  className?: string;
  onRemove?: (adId: string) => void;
}) => {
  const [preview, setPreview] = useState<{
    ad: SelectedAdAttachment;
    layoutId: string;
  } | null>(null);
  const [layoutTrigger, setLayoutTrigger] = useState(0);

  return (
    <>
      <div
        className={cn(
          `flex flex-nowrap items-center gap-2 overflow-x-auto scrollbar-hide`,
          className,
        )}
      >
        {ads.map((ad) => (
          <SelectedAdThumbnail
            key={ad.id}
            ad={ad}
            setPreview={(preview) => {
              setLayoutTrigger(Date.now());
              setPreview(preview);
            }}
            onRemove={onRemove}
            layoutTrigger={layoutTrigger}
          />
        ))}
      </div>

      {/* Fullscreen Preview - Rendered via Portal to document.body */}
      {createPortal(
        <AnimatePresence>
          {preview && (
            <>
              {/* Overlay */}
              <motion.div
                key="overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setLayoutTrigger(Date.now());
                  setPreview(null);
                }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
                style={{ pointerEvents: "auto" }}
              />

              {/* Enlarged Preview */}
              <motion.div
                key="previewWrapper"
                className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none"
              >
                <motion.div
                  className="w-fit relative pointer-events-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <motion.img
                    crossOrigin="anonymous"
                    src={withCacheBuster(preview.ad.thumbnailUrl)}
                    layoutId={preview.layoutId}
                    className="max-h-[80vh] max-w-[80vw] object-contain rounded-md pointer-events-auto"
                  />
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
};

const SelectedAdThumbnail = ({
  ad,
  setPreview,
  onRemove,
  layoutTrigger,
}: {
  ad: SelectedAdAttachment;
  setPreview: React.Dispatch<
    React.SetStateAction<{ ad: SelectedAdAttachment; layoutId: string } | null>
  >;
  onRemove?: (adId: string) => void;
  layoutTrigger: number;
}) => {
  const layoutId = useMemo(() => uuidv4(), []);

  return (
    <SqTooltipProvider>
      <SqTooltip>
        <SqTooltipTrigger asChild>
          <div
            className="relative group flex-none cursor-pointer"
            onClick={() => setPreview({ ad, layoutId })}
          >
            <motion.img
              crossOrigin="anonymous"
              src={withCacheBuster(ad.thumbnailUrl)}
              alt={ad.accountName}
              layoutId={layoutId}
              layoutDependency={layoutTrigger}
              className="h-[60px] w-[60px] rounded-[8px] border-[1px] border-[#E7E9EF] object-cover flex-shrink-0"
              style={{ aspectRatio: "1/1" }}
            />
            {/* Remove Button */}
            {onRemove && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(ad.id);
                }}
                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-[#09090B80] shadow-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <LuX size={12} />
              </button>
            )}
          </div>
        </SqTooltipTrigger>
        <SqTooltipContent side="top" align="end" portal={true}>
          {ad.accountName}
        </SqTooltipContent>
      </SqTooltip>
    </SqTooltipProvider>
  );
};

export default SelectedAdsPreview;
